/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { Image, Typography } from 'tfc-components';

import faqImage from 'assets/images/home/faq_images.png';
import Icon from 'components/atoms/Icon';
import Section from 'components/organisms/Section';

export interface IFaq {
  title: string;
  description: string[];
}

export interface IFaqCategory {
  title: string;
  faqs: IFaq[];
}

interface IFaqItemProps {
  list?: IFaq[] | IFaqCategory[];
  categoryName?: string;
  isHomePage?: boolean;
}

const faqData: IFaq[] = [
  {
    title: 'What is Ask2Auto?',
    description: ['Ask2Auto is an AI assistant that helps businesses automatically answer frequently asked questions from customers, employees, or partners.'],
  },
  {
    title: 'How does Ask2Auto work?',
    description: ['Ask2Auto uses AI to understand context and generate accurate answers based on the data you provide (websites, documents, internal systems).'],
  },
  {
    title: 'Can the Agent support multiple languages?',
    description: ['Yes. The Agent automatically detects the user’s language. If it cannot detect it, it will fall back to the default language you selected during setup.'],
  },
  {
    title: 'Can I embed Ask2Auto into my website?',
    description: ['Yes. We provide a script snippet to embed the chatbot in just a few lines of code.'],
  },
];

const FaqSection: React.FC<IFaqItemProps> = ({ list = faqData, categoryName = '', isHomePage = false }) => {
  const [activeIndex, setActiveIndex] = useState<string | null>(null);

  const isCategoryList = (item: IFaq | IFaqCategory): item is IFaqCategory => 'faqs' in item && Array.isArray((item as IFaqCategory).faqs);

  const handleToggle = (index: string) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <Section className="p-home_faq">
      {isHomePage && <Section.Title sectionName="FAQ" className="p-home_faq_titleSection">Frequently asked questions</Section.Title>}
      <div className="p-home_faq_wrapper">
        <div className="p-home_faq_image">
          <Image imgSrc={faqImage} alt="FAQ Illustration" ratio={2240 / 932} />
        </div>
        <div className="p-home_faq_content">
          {list && list.map((item, index) => (
            <React.Fragment key={`${item.title}-${index}`}>
              {categoryName !== '' && isCategoryList(item) && (
                <div className="p-home_faq_categoryWrap">
                  <div className="p-home_faq_dividerWrap">
                    <Typography.Text extendClasses="p-home_faq_title" fontweight="600">
                      {item.title}
                    </Typography.Text>
                    <div className="p-home_faq_divider" />
                  </div>
                  {item.faqs.map((faq, faqIndex) => {
                    const uniqueIndex = `${index}-${faqIndex}`;
                    return (
                      <div
                        key={`${item.title}-${faq.title}-${faqIndex}`}
                        className={`m-accordion_item ${activeIndex === uniqueIndex ? 'active' : ''}`}
                        role="button"
                        tabIndex={0}
                        aria-expanded={activeIndex === uniqueIndex}
                        aria-controls={`faq-content-${uniqueIndex}`}
                        onClick={() => handleToggle(uniqueIndex)}
                        onKeyDown={(e) => e.key === 'Enter' && handleToggle(uniqueIndex)}
                      >
                        <div className="m-accordion_item_title">
                          <Typography.Text fontweight="600" extendClasses="m-accordion_item_key">
                            {faq.title}
                          </Typography.Text>
                          <Icon iconName={activeIndex === uniqueIndex ? 'minusGray' : 'plusOrange'} size="24" />
                        </div>
                        {activeIndex === uniqueIndex && (
                          <div className="m-accordion_item_content" id={`faq-content-${uniqueIndex}`}>
                            {faq.description.map((line, lineIndex) => (
                              <Typography.Text
                                key={`${faq.title}-desc-${lineIndex}`}
                                extendClasses="m-accordion_item_text"
                              >
                                {line}
                              </Typography.Text>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {categoryName === '' && !isCategoryList(item) && (
                <div
                  key={`${item.title}-${index}`}
                  className={`m-accordion_item ${activeIndex === String(index) ? 'active' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-expanded={activeIndex === String(index)}
                  aria-controls={`faq-content-${index}`}
                  onClick={() => handleToggle(String(index))}
                  onKeyDown={(e) => e.key === 'Enter' && handleToggle(String(index))}
                >
                  <div className="m-accordion_item_title">
                    <Typography.Text fontweight="600" extendClasses="m-accordion_item_key">
                      {item.title}
                    </Typography.Text>
                    <Icon iconName={activeIndex === String(index) ? 'minusGray' : 'plusOrange'} size="24" />
                  </div>
                  {activeIndex === String(index) && (
                    <div className="m-accordion_item_content" id={`faq-content-${index}`}>
                      {item.description.map((line, lineIndex) => (
                        <Typography.Text
                          key={`${item.title}-desc-${lineIndex}`}
                          extendClasses="m-accordion_item_text"
                        >
                          {line}
                        </Typography.Text>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </Section>
  );
};

export default FaqSection;
